import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { useLanguage } from "@/contexts/LanguageContext";
import { useOperatorAssignment } from "@/hooks/useOperatorAssignment";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, UserCheck, UserX, RefreshCw, Briefcase } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";

const COLORS = {
  corte_plotter: "hsl(var(--chart-1))",
  laminador: "hsl(var(--chart-2))",
  taller: "hsl(var(--chart-3))",
  generico: "hsl(var(--chart-4))",
};

export default function OperatorAssignment() {
  const { t } = useLanguage();
  const { operators, distribution, loading, refresh } = useOperatorAssignment();

  const totalOperators = operators.length;
  const activeOperators = operators.filter(op => op.isActive).length;
  const inactiveOperators = totalOperators - activeOperators;

  const getAreaName = (area: string) => {
    return t.auth.areas[area as keyof typeof t.auth.areas] || area;
  };

  const getShiftName = (shift: string | null) => {
    if (!shift) return t.operatorAssignment.noShift;
    return t.operatorAssignment.shifts[shift as keyof typeof t.operatorAssignment.shifts] || shift;
  };

  const chartData = distribution.map((area) => ({
    name: getAreaName(area.area),
    value: area.total,
    color: COLORS[area.area as keyof typeof COLORS],
  }));

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-12 w-full" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              {t.operatorAssignment.title}
            </h1>
            <p className="text-muted-foreground mt-1">
              {t.operatorAssignment.subtitle}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={refresh}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            {t.common.loading}
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {t.operatorAssignment.totalOperators}
              </CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalOperators}</div>
              <p className="text-xs text-muted-foreground">
                {t.operatorAssignment.registered}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {t.operatorAssignment.activeOperators}
              </CardTitle>
              <UserCheck className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-500">{activeOperators}</div>
              <p className="text-xs text-muted-foreground">
                {t.operatorAssignment.working}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {t.operatorAssignment.inactiveOperators}
              </CardTitle>
              <UserX className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{inactiveOperators}</div>
              <p className="text-xs text-muted-foreground">
                {t.operatorAssignment.available}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Distribution by Area */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pie Chart */}
          <Card>
            <CardHeader>
              <CardTitle>{t.operatorAssignment.distributionByArea}</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Area Statistics */}
          <Card>
            <CardHeader>
              <CardTitle>{t.operatorAssignment.areaStatistics}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {distribution.map((area) => (
                <div key={area.area} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{getAreaName(area.area)}</span>
                    <span className="text-sm text-muted-foreground">
                      {area.total} {t.operatorAssignment.operators}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                      <div
                        className="h-full bg-green-500"
                        style={{ width: `${area.total > 0 ? (area.active / area.total) * 100 : 0}%` }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground min-w-[80px] text-right">
                      {area.active} {t.operatorAssignment.active} / {area.inactive} {t.operatorAssignment.inactive}
                    </span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Operator List */}
        <Card>
          <CardHeader>
            <CardTitle>{t.operatorAssignment.operatorList}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {operators.map((operator) => (
                <div
                  key={operator.id}
                  className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-2 h-2 rounded-full ${
                        operator.isActive ? "bg-green-500" : "bg-muted-foreground"
                      }`}
                    />
                    <div>
                      <p className="font-medium">{operator.full_name}</p>
                      <div className="flex gap-2 mt-1">
                        {operator.employee_id && (
                          <span className="text-xs text-muted-foreground">
                            ID: {operator.employee_id}
                          </span>
                        )}
                        {operator.area && (
                          <Badge variant="outline" className="text-xs">
                            {getAreaName(operator.area)}
                          </Badge>
                        )}
                        {operator.shift && (
                          <Badge variant="secondary" className="text-xs">
                            {getShiftName(operator.shift)}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    {operator.isActive ? (
                      <div className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4 text-green-500" />
                        <div>
                          <p className="text-sm font-medium text-green-500">
                            {t.operatorAssignment.working}
                          </p>
                          {operator.currentProcess && (
                            <p className="text-xs text-muted-foreground">
                              {operator.currentProcess}
                            </p>
                          )}
                          {operator.workOrderNumber && (
                            <p className="text-xs text-muted-foreground">
                              {t.operatorAssignment.order}: {operator.workOrderNumber}
                            </p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <Badge variant="secondary">{t.operatorAssignment.available}</Badge>
                    )}
                  </div>
                </div>
              ))}

              {operators.length === 0 && (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">
                    {t.operatorAssignment.noOperators}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}